/// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';

    export const BLOCK_NAMES_PROPERTY: string = "blockNames";

    export const ALPHA15: string = '15-ALPHA';
    export const ALPHA16: string = '16-ALPHA';
    export const ALPHA17: string = '17-ALPHA';
    export const max_blocks: number = 999;
    export const min_blocks: number = 1;

    export const DEFAULT_PORT: number = 1234;

    export interface INetworkNode extends ITopologyElement, INetworkElement, IGeoPositionable {
        creationDate: Date;
        lastUpdateDate: Date;
        mftSerialNumber: string;
        physicalLocation: string;
        type: Application.Models.INetworkNodeType;
        name: string;
        hostname: string;
        alias: string;
        active?: boolean;
        getDisplayLabel: () => string;
        config: INetworkNodeConfig;
        contactInfo: string;
        id?: string;
        ipAddressToShow?: string;
        typeId?: number;
        health: IDataNodeHealth;
        workflowMode: string;
        isMaintenance?: boolean;
        deviceGuiUrl?: string;
        deviceGuiUrlNoProxy?: string;
        isSonRelated?: boolean;
        useMag?: boolean;
        preferredMag?: string;
        canWrite?: boolean;
        gui?: IDeviceGui;
        evaluatedPermissions?: IEvaluatedAcl;
        vnos: Models.Vno[];
        isShared?: boolean;
        blocks?: Models.IBlock[];
        organizationId?: number;
        organizationName?: string;
        deviceCredentials?: IDeviceCredential[]
    }

    export interface INetworkInterface {
        id: number;
        name?: string;
        ipAddress?: string;
        networkConnectedTo?: Models.INetwork;
        dhcp?: boolean;
        connected?: boolean;
        connectedToInternet?: boolean;
        connectionOptions?: EurovisionControl.IRadioOption[];
        type: NetworkInterfaceType;
        multicastOrSrt?: string;
    }

    export interface INetworkNodeConfig {
        sonHostname?: string;
        srtVersion?: string;
        cpus?: number;
        numberOfBlocks?: number;
        useSon?: boolean;
        cpusOffset?: number;
        videoManager?: MVPVideoManagerConfig;
        portRangeStart?: number;
        blockNames?: IBlockNames;
        srtMulticastIpAndPort?: ITSIpAndPort;
        srtMulticastIterateIp?: boolean;
        isRedundant?: boolean;
        forwardTS?: boolean;
        mediakindStream?: number;
        mediakindPort?: number;
        mediakindStreamInUse?: number;
        mediakindPortInUse?: number;
        monitorMode?: boolean;
        disablePassphraseMod?: boolean;
        processRtp2Udp?: boolean;
    }


    export interface IBlockNames {
        generatedBlockNames: IBlockName[];
        dirty: boolean;
    }

    export interface IBlockName {
        id?: number;
        name: string;
        blockTypeId: number;
        appIndex: number;
        blockIndex: number;
        naturalKey: Models.BlockTypeNaturalKey;
        href?: string;
        typeToShow?: string;
        config?: Models.IBlockConfig;
        configAsJsonString?: string;
        lockedName: boolean;
        blockConnections?: Models.IBlockConnections[];
        inputTargetPort?: string;
        outputTargetPort?: string;
        customCallSrcPort?: string;
        isConnected?: boolean;
        autoRenamed?: boolean;
        presetPath?: string[];
        suffix?: string;
    }
   
    export class NetworkInterface implements INetworkInterface {
        public id: number;
        public name: string;
        public ipAddress: string;
        public networkConnectedTo: Models.INetwork;
        public dhcp: boolean;
        public connectionOptions: EurovisionControl.IRadioOption[];
        public type: NetworkInterfaceType;

        public static Empty(): NetworkInterface {
            let nic: NetworkInterface = new NetworkInterface();
            nic.id = -1;
            nic.name = '';
            nic.ipAddress = '';
            nic.networkConnectedTo = undefined;
            nic.dhcp = false;
            nic.connectionOptions = [];
            nic.type = NetworkInterfaceType.ANY;
            return nic;
        }

        public static createEmpty(): NetworkInterface {
            return new NetworkInterface();
        }

        public static createDefault(): NetworkInterface {
            let nic: NetworkInterface = new NetworkInterface();
            nic.name = "no_name";
            nic.ipAddress = "0.0.0.0";
            nic.dhcp = true;
            nic.connectionOptions = [
                {
                    value: 'Not connected',
                    selected: true,
                },
                {
                    value: 'Internet',
                },
            ];
            nic.type = NetworkInterfaceType.ANY;
            return nic;
        }

        constructor() {
            this.id = undefined;
        }
    }

    export class NetworkInterfaceType {
        public static ANY: string = "ANY";
        public static CONTROL: string = "CONTROL";
        public static DATA: string = "DATA";

        public static getValues(): NetworkInterfaceType[] {
            return [NetworkInterfaceType.ANY, 
                    NetworkInterfaceType.CONTROL, 
                    NetworkInterfaceType.DATA];
        }
    }

    export class NetworkNode implements INetworkNode {
        public id?: string;
        public name: string;
        public creationDate: Date;
        public lastUpdateDate: Date;
        public mftSerialNumber: string;
        public physicalLocation: string;
        public type: Application.Models.INetworkNodeType;
        public hostname: string;
        public alias: string;
        public href: string;
        public x: number;
        public y: number;
        public active: boolean;
        public networkInterfaces: INetworkInterface[];
        public defaultGatewayId: number;
        public geoElement: IGeoElement;
        public config: INetworkNodeConfig;
        public contactInfo: string;
        public health: IDataNodeHealth;
        public workflowMode: string;
        public isMaintenance: boolean;
        public blocks: Models.IBlock[];
        public useMag: boolean;
        public preferredMag: string;
        public canWrite: boolean;
        public gui?: IDeviceGui;
        public typeId?: number;
        public evaluatedPermissions?: IEvaluatedAcl;
        public vnos: Models.Vno[];
        public isShared: boolean;
        public organizationId: number;
        public organizationName: string;
        public mgmtServers?: Models.IMgmtServer[];
        public networkNodeTypeId?: number;
        public deviceCredentials?: IDeviceCredential[];

        public static getDefaultOrderColumn(): string {
            return "creationDate";
        }

        public static Empty(): NetworkNode {
            return new NetworkNode();
        }

        public static Copy(node: INetworkNode): NetworkNode {
            let newNode: NetworkNode = new NetworkNode();
            newNode.id = node.id;
            newNode.name = node.name;
            newNode.creationDate = node.creationDate;
            newNode.lastUpdateDate = node.lastUpdateDate;
            newNode.mftSerialNumber = node.mftSerialNumber;
            newNode.type = node.type;
            newNode.physicalLocation = node.physicalLocation;
            newNode.x = node.x;
            newNode.y = node.y;
            newNode.href = node.href;
            newNode.networkInterfaces = node.networkInterfaces;
            newNode.hostname = node.hostname;
            newNode.alias = node.alias;
            newNode.health = node.health;
            newNode.defaultGatewayId = node.defaultGatewayId;
            newNode.geoElement = node.geoElement;
            newNode.config = node.config;
            newNode.contactInfo = node.contactInfo;
            newNode.workflowMode = node.workflowMode;
            newNode.isMaintenance = node.isMaintenance;
            newNode.useMag = node.useMag;
            newNode.preferredMag = node.preferredMag === null ? undefined: node.preferredMag; // tslint:disable-line
            newNode.vnos = !node.vnos ? [] : node.vnos;
            newNode.isShared = node.isShared;
            newNode.organizationId = node.organizationId;
            newNode.organizationName = node.organizationName;
            return newNode;
        }

        /**
         * NetworkNode's empty constructor for automatic conversion from
         * REST data.
         */
        constructor() {
            this.href = '';
            this.name = '';
            this.creationDate = new Date();
            this.lastUpdateDate = new Date();
            this.mftSerialNumber = '';
            this.physicalLocation = '';
            this.hostname = undefined;
            this.alias = undefined;
            this.health = {
                data: {
                    healthStatus: 'PENDING',
                    checks: [],
                    elementId: '',
                },
            };
            this.defaultGatewayId = undefined;
            this.geoElement = undefined;
            this.config = {
                useSon: false,
                srtMulticastIterateIp: false
            };
            this.contactInfo = "";
            this.workflowMode = 'operation';
            this.isMaintenance = false;
            this.useMag = false;
            this.preferredMag = undefined;
            this.vnos = [];
            this.isShared = false;
        }

        public validate(): boolean {
            return this.name !== '' &&
                this.creationDate !== new Date() &&
                this.mftSerialNumber !== '' &&
                this.type !== undefined &&
                this.physicalLocation !== '';
        }

        public getId(): number {
            return Common.Utils.idFromHref(this.href);
        }
        
        public getAvailableInterfaces(): INetworkInterface[] {
            return this.networkInterfaces.filter (
                (nic: INetworkInterface) => {
                    /* tslint:disable */return nic.networkConnectedTo === null;/* tslint:enable */
                }
            );
        }
        
         public isConnectedToNetwork(): boolean {
            return this.networkInterfaces.some (
                (nic: INetworkInterface) => {
                    /* tslint:disable */
                    return nic !== undefined &&
                        nic.networkConnectedTo !== undefined && nic.networkConnectedTo !== null;
                    /* tslint:enable */
                }
            );
        }
        
        
        public getDisplayLabel(): string {
            return this.name + (this.alias && this.alias !== '' ? '\n(' + this.alias + ')' : '');
        }
        
        public getDisplayImage(): string {
            let status: string = this.health !== undefined ? this.health.data.healthStatus : undefined;
            if (status !== "ONLINE" && status !== "OFFLINE")
                status = "PENDING";

            let typeId: number = this.type.id;

            if (typeId === undefined) { 
                let id: number = Common.Utils.idFromHref(this.type.href);
                if (!isNaN(id)) {
                    typeId = id;
                }
                else
                    return "images/iconmonstr-server-4-240-not-checked.png";
            }

            return `images/nodeTypes/${typeId}-${status}.png`;
        }

        public class(): TopologyElementType {
            return TopologyElementType.NetworkNode;
        }

        public setDefaultNames(blockNames: IBlockNames): void {
            this.config[BLOCK_NAMES_PROPERTY] = blockNames;
        }

        public getVnoNames(): string {
            return this.isShared && this.vnos.length === 1 ? 'shared' : this.vnos.map((vno: Models.Vno) => (vno.name)).join(', ');
        }

        public toString(): string {
            return this.name;
        }
    }

    export class MVPConfig implements INetworkNodeConfig {
        public videoManager: MVPVideoManagerConfig;
    }

    export class MVPVideoManagerConfig {
        public enabled: boolean;
        public host: string;
        public port: number;
    }


    export interface IMgmtServer {
        index: number;
        name: string;
        selected?: boolean;
        changing?: boolean;
    }

    export interface IMgmtServers {
        [key: number]: IMgmtServer;
    }
}
